"use client";

import React, { useState, useEffect } from "react";
import { Mail, ArrowRight } from "lucide-react";
import Link from "next/link";
import { useTheme } from "next-themes";
import { SEO_CONFIG } from "@/lib/seo-config";
import { getBlogBaseUrl } from "@/lib/utils/blog-url";
import { subscribeEmail } from "@/lib/actions/subscribe";

export default function BlogFooter() {
  const { theme, resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    setMounted(true);
  }, []);

  const logoSrc = mounted
    ? resolvedTheme === "dark"
      ? "/logo-dark.webp"
      : "/logo-light.webp"
    : "/logo-dark.webp";

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      const result = await subscribeEmail(email, "blog_footer");
      if (result.error) {
        setMessage(result.error);
      } else {
        setMessage("✓ Thanks for subscribing!");
        setEmail("");
        setTimeout(() => setMessage(""), 3000);
      }
    } catch (error) {
      setMessage("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative mt-12 md:mt-16">
      {/* Top Border with Gradient Accent */}
      <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-border/50 to-transparent" />

      <div className="max-w-6xl mx-auto px-6 pt-10 md:pt-16 pb-6 md:pb-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-start">
          {/* Column 1: Brand & About */}
          <div className="md:col-span-5 space-y-6">
            <a
              href={SEO_CONFIG.domain}
              className="group inline-flex flex-col gap-2"
            >
              <img
                src={logoSrc}
                alt="Logo"
                loading="lazy"
                className="h-10 md:h-16 w-auto transition-transform duration-500 group-hover:scale-[1.02]"
              />
            </a>

            <p className="text-sm md:text-base text-muted-foreground/80 leading-relaxed max-w-sm">
              Technical articles and development insights. Exploring software
              architecture, mobile development, and building great products.
            </p>

            <div className="pt-2 flex flex-col gap-2">
              <span className="text-[10px] font-mono font-bold text-muted-foreground/40 uppercase tracking-[0.2em]">
                Projects & Ventures
              </span>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-sm">
                <span className="text-muted-foreground/60 font-medium">Founder of</span>
                <a
                  href="https://mojostudio.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group/link relative inline-flex items-center gap-0.5 text-primary font-bold hover:text-foreground transition-colors duration-300"
                >
                  mojostudio.in
                  <span className="absolute bottom-0 left-0 w-full h-[1px] bg-primary/30 origin-bottom-right scale-x-0 transition-transform duration-300 group-hover/link:scale-x-100 group-hover/link:origin-bottom-left" />
                </a>
                <span className="text-muted-foreground/30 font-light">,</span>
                <a
                  href="https://mojodocs.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group/link relative inline-flex items-center gap-0.5 text-primary font-bold hover:text-foreground transition-colors duration-300"
                >
                  mojodocs.in
                  <span className="absolute bottom-0 left-0 w-full h-[1px] bg-primary/30 origin-bottom-right scale-x-0 transition-transform duration-300 group-hover/link:scale-x-100 group-hover/link:origin-bottom-left" />
                </a>
                <span className="text-muted-foreground/30 font-light">&</span>
                <a
                  href="https://vanisagar.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group/link relative inline-flex items-center gap-0.5 text-primary font-bold hover:text-foreground transition-colors duration-300"
                >
                  vanisagar.in
                  <span className="absolute bottom-0 left-0 w-full h-[1px] bg-primary/30 origin-bottom-right scale-x-0 transition-transform duration-300 group-hover/link:scale-x-100 group-hover/link:origin-bottom-left" />
                </a>
              </div>
            </div>
          </div>

          {/* Right Section Grid */}
          <div className="md:col-span-7 grid grid-cols-2 gap-8 md:gap-12 w-full">
            {/* Column 2: Directory */}
            <div className="space-y-6">
              <h3 className="text-[11px] font-black uppercase tracking-[0.2em] text-foreground border-b border-border pb-3">
                Directory
              </h3>
              <ul className="space-y-4">
                {[
                  { name: "Home", href: SEO_CONFIG.domain },
                  { name: "All Articles", href: getBlogBaseUrl() },
                  { name: "Projects", href: `${SEO_CONFIG.domain}/projects` },
                  { name: "Hire Me", href: `${SEO_CONFIG.domain}/hire` },
                  { name: "About", href: `${SEO_CONFIG.domain}/about` },
                  { name: "FAQ", href: `${SEO_CONFIG.domain}/faq` },
                  { name: "Free Tools", href: `${SEO_CONFIG.domain}/tools` },
                  { name: "Rate Guides", href: `${SEO_CONFIG.domain}/guides` },
                  { name: "Glossary", href: `${SEO_CONFIG.domain}/glossary` },
                  {
                    name: "Privacy Policy",
                    href: `${SEO_CONFIG.domain}/privacy-policy`,
                  },
                  { name: "Contact", href: `${SEO_CONFIG.domain}/contact` },
                ].map((link) => (
                  <li key={link.name}>
                    <a
                      href={link.href}
                      className="group flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-all duration-300"
                    >
                      <span className="w-0 group-hover:w-3 h-[1px] bg-primary transition-all duration-300" />
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 3: Social Hub */}
            <div className="space-y-6">
              <h3 className="text-[11px] font-black uppercase tracking-[0.2em] text-foreground border-b border-border pb-3">
                Social Hub
              </h3>
              <ul className="space-y-4">
                {[
                  { name: "GitHub", href: SEO_CONFIG.social.github },
                  { name: "LinkedIn", href: SEO_CONFIG.social.linkedin },
                  { name: "Twitter", href: SEO_CONFIG.social.twitter },
                  { name: "Email Address", href: `mailto:${SEO_CONFIG.email}` },
                ].map((link) => (
                  <li key={link.name}>
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-all duration-300"
                    >
                      <span className="w-0 group-hover:w-3 h-[1px] bg-primary transition-all duration-300" />
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-border/10 flex flex-col md:flex-row justify-between items-center gap-6">
          <p className="text-[10px] md:text-xs font-medium text-muted-foreground/60 tracking-wider">
            © {currentYear} {SEO_CONFIG.name}. ALL RIGHTS RESERVED.
          </p>

          <div className="flex items-center gap-6">
            {[
              { name: "Home", href: SEO_CONFIG.domain },
              { name: "Privacy", href: `${SEO_CONFIG.domain}/privacy-policy` },
              { name: "Disclaimer", href: `${SEO_CONFIG.domain}/disclaimer` },
              { name: "Copyright Policy", href: `${SEO_CONFIG.domain}/legal/copyright-policy` },
              { name: "Contact", href: `${SEO_CONFIG.domain}/contact` },
            ].map((item) => (
              <a
                key={item.name}
                href={item.href}
                className="text-[10px] font-bold text-muted-foreground/40 hover:text-foreground transition-colors uppercase tracking-[0.1em]"
              >
                {item.name}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
